import { useState } from "react";
import { Modal } from "semantic-ui-react";
import { v4 as uuidv4 } from "uuid";

import { useCan } from "components/Can";
import { InterventionModalForm } from "components/InterventionModal/components/InterventionModalForm";
import { useInterventionPrice } from "components/InterventionModal/hooks";
import "components/InterventionModal/InterventionModal.scss";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES, PIN_STATUSES, PIN_TYPES, WARRANTY_TYPES } from "models";
import { useAppendPinMutation } from "modules/AppointmentDetails/components/Interventions/hooks";
import { useAddInterventionMutation } from "modules/AppointmentDetails/hooks";
import { generateDMSNr } from "utils/appointmentUtils";

import type { Dealer, DealerLocation, Intervention, InterventionAttachment, InterventionElement } from "models";
import type { AppendPinPayload } from "modules/AppointmentDetails/components/Interventions/hooks";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";
import type { ModalProps } from "semantic-ui-react";

export type AddInterventionAndAppendPinPayloads = {
  intervention: AddInterventionPayload;
  pin?: AppendPinPayload;
};

export type InterventionFormAttachment = Omit<InterventionAttachment, "user" | "is_final_car_check" | "intervention_id" | "id" | "user_id"> & {
  user_id?: number;
};

export type InterventionModalFormValues = Pick<Intervention, "title" | "price" | "customer_ok" | "description"> & {
  pin_type_id: PIN_TYPES | null;
  attachments: InterventionFormAttachment[];
};

type InterventionModalProps = {
  open: boolean;
  carId: number;
  modalProps?: ModalProps;
  intervention?: AddInterventionPayload | null;
  isCustomerOk?: boolean;
  appointmentId: number;
  dealer?: Dealer | null;
  location?: DealerLocation | null;
  pinTypeId?: PIN_TYPES;
  showFileUpload?: boolean;
  showAddSubitems?: boolean;
  isCarRecallPinType?: boolean;
  canWriteBackCustomerData?: boolean;
  onClose: () => void;
  onSubmit?: (interventionPinned: AddInterventionAndAppendPinPayloads) => void;
  onInterventionCreated?: (data: AddInterventionPayload) => void;
};

const FormHelper = createFormHelper<InterventionModalFormValues>();

export const InterventionModal = ({
  open,
  location,
  dealer,
  modalProps,
  intervention,
  appointmentId,
  carId,
  pinTypeId,
  isCustomerOk,
  showAddSubitems,
  canWriteBackCustomerData,
  showFileUpload = true,
  isCarRecallPinType = false,
  onClose,
  onSubmit,
  onInterventionCreated
}: InterventionModalProps) => {
  const { user } = usePermanentRealTimeUser();
  const { getInterventionPrice } = useInterventionPrice(location);
  const { mutateAsync: addIntervention, isPending: isAddInterventionPending } = useAddInterventionMutation();
  const { mutateAsync: appendPin, isPending: isAppendPinPending } = useAppendPinMutation();
  const canUpdateIntervention = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.Interventions);
  const canCreateFullPin = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.PinFull);

  const [hasErrors, setHasErrors] = useState(false);
  const [interventionSubitems, setInterventionSubitems] = useState<InterventionElement[]>([]);

  const handleUpdateInterventionElements = (interventionSubitems: InterventionElement[]) => setInterventionSubitems(interventionSubitems);

  const getAppendPinPayload = (intervention_id: number, pinType: PIN_TYPES | null) => ({
    pin_type_id: pinType ? pinType : PIN_TYPES.Remarks,
    warranty_type_id: WARRANTY_TYPES.NotSet,
    pin_status_id: PIN_STATUSES.NotSet,
    order_status: null,
    intervention_id,
    appointment_id: appointmentId,
    dealer_location_id: location?.id ?? -1,
    car_id: carId,
    user_id: user?.id ?? -1,
    claim_nr: "",
    ref_nr: "",
    support_nr: "",
    note: "",
    visible_important_items: false,
    keep_parts: false,
    recalls_id: intervention?.recalls_id ?? "",
    pon_recall_id: intervention?.pon_recall_id ?? "",
    rdw_recall_id: intervention?.rdw_recall_id ?? ""
  });

  const handleAppendPin = async (intervention_id: number, pinType: PIN_TYPES | null) => {
    if (!canCreateFullPin) return;
    await appendPin(getAppendPinPayload(intervention_id, pinType));
  };

  const getAddInterventionPayload = ({ customer_ok, description, title, price, attachments }: InterventionModalFormValues): AddInterventionPayload => ({
    title,
    customer_ok,
    description,
    price: getInterventionPrice(price),
    appointment_id: intervention?.appointment_id || appointmentId || -1,
    dms_nr: intervention?.dms_nr || generateDMSNr(),
    question_result_status: intervention?.question_result_status,
    replacements: intervention?.replacements,
    elements: interventionSubitems,
    question_result_id: intervention?.question_result_id,
    check_in_remark_id: intervention?.check_in_remark_id,
    keylocker_remark_id: intervention?.keylocker_remark_id,
    kiosk_remark_id: intervention?.kiosk_remark_id,
    check_in_result_id: intervention?.check_in_result_id,
    diagnose_overview_remark_id: intervention?.diagnose_overview_remark_id,
    snooze_id: intervention?.snooze_id,
    attachments
  });

  const handleAddInterventionAndAppendPin = async (values: InterventionModalFormValues) => {
    if (!canUpdateIntervention) return;
    const addInterventionPayload = getAddInterventionPayload(values);
    const { id: intervention_id } = await addIntervention(addInterventionPayload);

    (values.pin_type_id || isCarRecallPinType) && (await handleAppendPin(intervention_id, values.pin_type_id));
    onInterventionCreated?.(addInterventionPayload);
    onClose();
  };

  const getAddInterventionAndAppendPinPayloads = (values: InterventionModalFormValues): AddInterventionAndAppendPinPayloads => ({
    intervention: { ...getAddInterventionPayload(values), reference_nr: `claire_${uuidv4()}`, visible_to_customer: true },
    pin: values.pin_type_id && canCreateFullPin ? getAppendPinPayload(-1, values.pin_type_id) : undefined
  });

  const handleClose = () => {
    setInterventionSubitems([]);
    onClose();
  };

  const isFormInvalid = (values: InterventionModalFormValues) => {
    const isInvalid = values.title.length < 1;
    setHasErrors(isInvalid);
    return isInvalid;
  };

  const handleSubmit = async (values: InterventionModalFormValues) => {
    if (isFormInvalid(values) || isAddInterventionPending) return;

    if (onSubmit) {
      onSubmit(getAddInterventionAndAppendPinPayloads(values));
      handleClose();
      return;
    }

    await handleAddInterventionAndAppendPin(values);
  };

  return (
    <Modal open={open} onClose={handleClose} closeOnDimmerClick={false} className="InterventionModal" size="large" {...modalProps}>
      <FormHelper.Form
        onSubmit={handleSubmit}
        defaultValues={{
          customer_ok: intervention?.customer_ok ?? isCustomerOk ?? false,
          attachments: intervention?.attachments ?? [],
          description: intervention?.description ?? "",
          price: intervention?.price ?? 0,
          title: intervention?.title ?? "",
          pin_type_id: pinTypeId ?? (isCarRecallPinType ? PIN_TYPES.Recall : null)
        }}
      >
        <InterventionModalForm
          dealer={dealer}
          location={location}
          isLoading={isAddInterventionPending || isAppendPinPending}
          hasErrors={hasErrors}
          intervention={intervention}
          appointmentId={appointmentId}
          showFileUpload={showFileUpload}
          showAddSubitems={showAddSubitems}
          isCarRecallPinType={isCarRecallPinType}
          interventionSubitems={interventionSubitems}
          canWriteBackCustomerData={canWriteBackCustomerData}
          onClose={handleClose}
          onSubmit={handleSubmit}
          handleUpdateInterventionElements={handleUpdateInterventionElements}
        />
      </FormHelper.Form>
    </Modal>
  );
};
